import { MONSTER_PARTY_ASSET_KEYS } from '../assets/asset-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { ExpBar } from '../common/exp-bar.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import { DataUtils } from '../utils/data-utils.js';
import { calculateExpBarCurrentValue, expNeededForNextLevel } from '../utils/leveling-utils.js';
import { BaseScene } from './base-scene.js';
import { SCENE_KEYS } from './scene-keys.js';
import { fitCameraToDesign } from '../utils/fit-camera.js';
import { makeButton } from '../utils/ui-button.js';
import { feedMonster } from '../net/api.js';
import { netManager } from '../net/index.js';

// The monster-details layout is authored against the 1024x576 background image.
const DESIGN_WIDTH = 1024;
const DESIGN_HEIGHT = 576;

/** @type {Phaser.Types.GameObjects.Text.TextStyle} */
const UI_TEXT_STYLE = {
  fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
  color: '#FFFFFF',
  fontSize: '24px',
};

/** @type {Phaser.Types.GameObjects.Text.TextStyle} */
const MONSTER_MOVE_TEXT_STYLE = {
  fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
  color: '#000000',
  fontSize: '40px',
};

/** @type {Phaser.Types.GameObjects.Text.TextStyle} */
const MONSTER_EXP_TEXT_STYLE = {
  fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
  color: '#000000',
  fontSize: '22px',
};

/**
 * @typedef MonsterDetailsSceneData
 * @type {object}
 * @property {import('../types/typedef.js').Monster} monster
 */

export class MonsterDetailsScene extends BaseScene {
  /** @type {import('../types/typedef.js').Monster} */
  #monsterDetails;
  /** @type {import('../types/typedef.js').Attack[]} */
  #monsterAttacks;
  /** @type {Phaser.GameObjects.Image} */
  #monsterImage;
  /** @type {Phaser.GameObjects.Text} */
  #bondValueText;
  /** @type {Phaser.GameObjects.Rectangle} */
  #bondBarFill;
  /** @type {boolean} */
  #feeding = false;

  constructor() {
    super({
      key: SCENE_KEYS.MONSTER_DETAILS_SCENE,
    });
  }

  /**
   * @param {MonsterDetailsSceneData} data
   * @returns {void}
   */
  init(data) {
    super.init(data);

    this.#monsterDetails = data.monster;

    // added for testing from preload scene directly
    if (this.#monsterDetails === undefined) {
      this.#monsterDetails = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY)[0];
    }

    this.#monsterAttacks = [];
    this.#monsterDetails.attackIds.forEach((attackId) => {
      const monsterAttack = DataUtils.getMonsterAttack(this, attackId);
      if (monsterAttack !== undefined) {
        this.#monsterAttacks.push(monsterAttack);
      }
    });
  }

  /**
   * @returns {void}
   */
  create() {
    super.create();

    // fill the viewport with the fixed 1024x576 design-space layout (and re-fit on resize)
    fitCameraToDesign(this, DESIGN_WIDTH, DESIGN_HEIGHT);

    // create main background and title
    this.add.image(0, 0, MONSTER_PARTY_ASSET_KEYS.MONSTER_DETAILS_BACKGROUND).setOrigin(0);
    this.add.text(10, 0, 'Monster Details', {
      ...UI_TEXT_STYLE,
      fontSize: '48px',
    });

    // add monster details
    this.add.text(20, 60, `Lv. ${this.#monsterDetails.currentLevel}`, {
      ...UI_TEXT_STYLE,
      fontSize: '40px',
    });
    this.add.text(200, 60, this.#monsterDetails.name, {
      ...UI_TEXT_STYLE,
      fontSize: '40px',
    });
    // The portrait is tappable — petting it plays a happy reaction (free "interact with them" touch).
    this.#monsterImage = this.add.image(160, 310, this.#monsterDetails.assetKey).setOrigin(0, 1).setScale(0.7);
    this.#monsterImage.setInteractive({ useHandCursor: true });
    this.#monsterImage.on('pointerdown', () => this.#reaction());

    if (this.#monsterAttacks[0] !== undefined) {
      this.add.text(560, 82, this.#monsterAttacks[0].name, MONSTER_MOVE_TEXT_STYLE);
    }

    if (this.#monsterAttacks[1] !== undefined) {
      this.add.text(560, 162, this.#monsterAttacks[1].name, MONSTER_MOVE_TEXT_STYLE);
    }

    if (this.#monsterAttacks[2] !== undefined) {
      this.add.text(560, 242, this.#monsterAttacks[2].name, MONSTER_MOVE_TEXT_STYLE);
    }

    if (this.#monsterAttacks[3] !== undefined) {
      this.add.text(560, 322, this.#monsterAttacks[3].name, MONSTER_MOVE_TEXT_STYLE);
    }

    // add monster exp details
    this.add.text(20, 340, 'Current Exp.', MONSTER_EXP_TEXT_STYLE).setOrigin(0, 0);
    this.add.text(516, 340, `${this.#monsterDetails.currentExp}`, MONSTER_EXP_TEXT_STYLE).setOrigin(1, 0);
    this.add.text(20, 365, 'Exp. to next level', MONSTER_EXP_TEXT_STYLE);
    this.add
      .text(
        516,
        365,
        `${expNeededForNextLevel(this.#monsterDetails.currentLevel, this.#monsterDetails.currentExp)}`,
        MONSTER_EXP_TEXT_STYLE
      )
      .setOrigin(1, 0);
    this.add.text(108, 392, 'EXP', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      color: '#6505FF',
      fontSize: '14px',
      fontStyle: 'italic',
    });
    const expBar = new ExpBar(this, 70, 200);
    expBar.setMeterPercentageAnimated(
      calculateExpBarCurrentValue(this.#monsterDetails.currentLevel, this.#monsterDetails.currentExp),
      { skipBattleAnimations: true }
    );

    this.#renderBondSection();
    this.scene.bringToTop(SCENE_KEYS.MONSTER_DETAILS_SCENE);
  }

  /** Current bond (0-100), tolerant of legacy monsters saved before bond existed. */
  #bond() {
    const b = Number(this.#monsterDetails.bond ?? 0);
    return Number.isFinite(b) ? Math.max(0, Math.min(100, b)) : 0;
  }

  /** Draw the Bond meter + the Feed button (bottom-right of the details sheet). */
  #renderBondSection() {
    const bond = this.#bond();
    this.add.text(560, 400, '❤ Bond', { ...MONSTER_EXP_TEXT_STYLE });
    this.#bondValueText = this.add.text(942, 400, `${bond} / 100`, { ...MONSTER_EXP_TEXT_STYLE }).setOrigin(1, 0);
    // bar track + fill
    this.add.rectangle(560, 432, 382, 16, 0x241b2e).setOrigin(0, 0.5).setStrokeStyle(2, 0x5a3f6e);
    this.#bondBarFill = this.add.rectangle(561, 432, 380 * (bond / 100), 12, 0xff5e8a).setOrigin(0, 0.5);

    makeButton(this, {
      x: 752,
      y: 500,
      width: 384,
      height: 48,
      label: '🍖 Feed  ·  20 Gold',
      primary: true,
      fontSize: 22,
      onClick: () => void this.#feed(),
    });
  }

  /** Pay Gold to feed the monster: raises bond (caps at 100) + plays a happy reaction. */
  async #feed() {
    if (this.#feeding) return;
    if (this.#bond() >= 100) {
      this.#flash('Already fully bonded! ❤');
      return;
    }
    this.#feeding = true;
    const res = await feedMonster(netManager?.wallet ?? null);
    this.#feeding = false;
    // The player may have backed out while the request was in flight — never touch dead game objects.
    if (!this.sys?.isActive?.()) return;
    if (!res || !res.ok) {
      this.#flash(res?.reason === 'insufficient' ? 'Not enough Gold (need 20).' : 'Could not feed — try again.');
      return;
    }
    const newBond = Math.min(100, this.#bond() + 15);
    this.#monsterDetails.bond = newBond;
    // Persist into the saved party (the autosave then syncs it server-side).
    const party = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY);
    if (Array.isArray(party)) {
      const idx = party.findIndex((m) => m && m.id === this.#monsterDetails.id);
      if (idx >= 0) party[idx].bond = newBond;
      dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, party);
      dataManager.saveData();
    }
    if (typeof res.gold === 'number') dataManager.store.set(DATA_MANAGER_STORE_KEYS.GOLD, res.gold);
    this.#bondValueText.setText(`${newBond} / 100`);
    this.#bondBarFill.width = 380 * (newBond / 100);
    this.#reaction();
    this.#flash(newBond >= 100 ? 'Fully bonded! ❤' : 'Yum! +15 bond');
  }

  /** A happy little reaction: the portrait bounces and a heart floats up. */
  #reaction() {
    if (this.#monsterImage) {
      this.tweens.add({
        targets: this.#monsterImage,
        scale: { from: 0.66, to: 0.7 },
        duration: 140,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
      const heart = this.add.text(this.#monsterImage.x + 90, 230, '❤', { fontSize: '40px', color: '#ff5e8a' }).setOrigin(0.5);
      this.tweens.add({
        targets: heart,
        y: 150,
        alpha: { from: 1, to: 0 },
        duration: 900,
        ease: 'Quad.easeOut',
        onComplete: () => heart.destroy(),
      });
    }
  }

  /** Brief status line under the Feed button. */
  #flash(message) {
    const t = this.add
      .text(752, 544, message, { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '18px', color: '#ffe14d' })
      .setOrigin(0.5);
    this.tweens.add({ targets: t, alpha: { from: 1, to: 0 }, delay: 1400, duration: 600, onComplete: () => t.destroy() });
  }

  /**
   * @returns {void}
   */
  update() {
    super.update();

    if (this._controls.isInputLocked) {
      return;
    }

    if (this._controls.wasBackKeyPressed()) {
      this.#goBackToPreviousScene();
      return;
    }

    if (this._controls.wasSpaceKeyPressed()) {
      this.#goBackToPreviousScene();
      return;
    }
  }

  /**
   * @returns {void}
   */
  #goBackToPreviousScene() {
    this._controls.lockInput = true;
    this.scene.stop(SCENE_KEYS.MONSTER_DETAILS_SCENE);
    this.scene.resume(SCENE_KEYS.MONSTER_PARTY_SCENE);
  }
}
